import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AJAX from 'common/AJAX';
import MultiLineChart from 'common/charts/MultiLineChart';
import Link from 'common/Link';
import SpinnerV2 from 'common/SpinnerV2';
import { P } from 'common/ui/Text';
import VStack from 'common/uiV3/VStack';

import type { Period } from 'common/charts/Period';

type ThemeActivityResults = {
  periods: string[];
  themes: {
    _id: string;
    name: string;
    counts: number[];
  }[];
};

export default function AdminDashboardThemeActivity() {
  const [period] = useState<Period>('month');
  const [themeActivity, setThemeActivity] = useState<ThemeActivityResults | null>(null);

  const fetchThemeActivity = useCallback(async () => {
    const responseJSON = await AJAX.post('/api/reporting/getThemeActivity', {
      period,
    });
    const response = JSON.parse(responseJSON);
    setThemeActivity(response.results as ThemeActivityResults);
  }, [period]);

  useEffect(() => {
    fetchThemeActivity();
  }, [fetchThemeActivity]);

  const [data, labels, themeNames] = useMemo(() => {
    if (!themeActivity) {
      return [null, null, null];
    }

    const data = themeActivity.themes.map((theme) => theme.counts);
    const labels = themeActivity.periods.map((period) => {
      return new Date(period).toLocaleDateString('en-US', {
        month: 'short',
      });
    });
    const themeNames = themeActivity.themes.map((theme) => theme.name);
    return [data, labels, themeNames];
  }, [themeActivity]);

  const isLoading = !themeActivity || !labels || !data;

  const chart = isLoading ? (
    <SpinnerV2 className="mt-4" size="medium" />
  ) : themeNames.length === 0 ? (
    <VStack className="flex-1 items-center justify-center py-8">
      <div className="text-center">
        <P>We don't have enough data to display theme trends yet.</P>
        <P>
          <Link
            className="text-body underline text-indigo-90 visited:text-violet-90 hover:text-indigo-110 active:text-indigo-100 transition-all duration-100 ease-in-out"
            to="/admin/settings/fields/themes">
            Add or manage your themes
          </Link>{' '}
          to start tracking trends over time.
        </P>
      </div>
    </VStack>
  ) : (
    <MultiLineChart labels={labels} data={data} dataLabels={themeNames} />
  );

  return (
    <VStack className="base-border gap-4 rounded-sm p-4 mb-4 relative min-h-[320px]">
      <h3 className="text-2xl">Theme trends</h3>
      {chart}
    </VStack>
  );
}
